import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Mark } from "./Mark";
import { scrollToId } from "@/lib/lenis";

export const LINKS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Our Services" },
  { id: "markets", label: "Markets" },
  { id: "about", label: "About Us" },
  { id: "pay", label: "Pay a Bill" },
  { id: "contact", label: "Contact Us" },
];

export const useSectionNav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (id) => {
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const go = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const pick = (id) => { setOpen(false); go(id); };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${scrolled ? "bg-[#070E17]/75 backdrop-blur-xl border-[rgba(212,175,55,0.15)]" : "bg-transparent border-transparent"}`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" onClick={() => pick("home")} className="flex items-center gap-3" data-testid="nav-logo-link">
            <Mark />
            <span className="leading-none">
              <span className="font-serif-d block text-xl font-semibold tracking-wide text-white">VK Enterprises</span>
              <span className="font-mono-d block text-[10px] uppercase tracking-[0.3em] text-[#E6C280]">Property Consultant</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.slice(1).map((l) => (
              <button key={l.id} onClick={() => pick(l.id)} className="group relative text-sm text-slate-300 transition-colors duration-300 hover:text-white" data-testid={`nav-link-${l.id}`}>
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-[#D4AF37] transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="font-mono-d hidden rounded-full border border-[rgba(212,175,55,0.3)] px-3 py-1.5 text-[11px] tracking-[0.2em] text-[#E6C280] md:inline-block" data-testid="nav-markets-pill">US · UK · NL</span>
            <button onClick={() => pick("contact")} className="group hidden items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-[#070E17] transition-[background-color,transform] duration-300 hover:bg-[#E6C280] active:scale-95 sm:flex" data-testid="nav-enquire-btn">
              Enquire <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button onClick={() => setOpen(true)} className="rounded-full border border-[rgba(212,175,55,0.3)] p-2.5 text-white lg:hidden" aria-label="Open menu" data-testid="nav-mobile-open">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#0B192C] px-6 pb-10 pt-6"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Mark />
              <button onClick={() => setOpen(false)} className="rounded-full border border-[rgba(212,175,55,0.3)] p-2.5" aria-label="Close menu" data-testid="nav-mobile-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-16 flex flex-col gap-4">
              {LINKS.map((l, i) => (
                <motion.button key={l.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07 }} onClick={() => pick(l.id)} className="font-serif-d text-left text-5xl text-white" data-testid={`mobile-link-${l.id}`}>
                  <span className="font-mono-d mr-4 align-middle text-xs text-[#D4AF37]">0{i + 1}</span>{l.label}
                </motion.button>
              ))}
            </div>
            <div className="mt-auto text-sm text-slate-300">
              <p>support@vkenterpriseconsult.com</p>
              <p>+91 87968 81958</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
